import { crudTable, date, index, integer, text } from "@-/dev.improve.haniverse.lib.database.table";
import { sql } from "@-/com.npmjs.drizzle-orm";

/**
 * A user card represents a user-owned study flashcard that they can make SRS progress towards, and eventually master.
 */
export const userCardTable = crudTable(
  "user_card",
  {
    /**
     * When a "skip until tomorrow" from the new-card flow ends. The card stays out of lesson
     * selection until this instant, then becomes eligible again — including from BEFORE the
     * learner's current position in the sequence. Stored as the absolute instant (computed once
     * at the skip, as the start of the next LOCAL calendar day) so a restarted app and a synced
     * device release it at the same moment. Null when not deferred; cleared when the card is
     * enrolled. Unlike `skippedAt` it is a temporary deferral, not a removal from the flow.
     */
    deferredUntil: date("deferred_until"),

    /** FSRS scheduler row when the card is in study; null otherwise */
    // A parent id, not a FOREIGN KEY: see `ACTOR_TABLE_PARENTS` (the cloud copy would enforce one).
    fsrsCardId: text("fsrs_card_id"),

    /** When the user manually marked this card as mastered, bypassing SRS; null otherwise */
    manuallyMasteredAt: date("manually_mastered_at"),

    /**
     * When the learner asked to learn this (meaning) card but a free learner had already hit the
     * day's new-grapheme limit, so it waits in the learn queue instead of enrolling. Queued cards
     * are enrolled first, oldest first, as soon as the daily allowance has room again. Null when
     * not queued; cleared when the card is enrolled.
     */
    queuedAt: date("queued_at"),

    /** When the user skipped this card from the new-card flow; updated on repeat skips */
    skippedAt: date("skipped_at"),

    /**
     * Review recharge (0–100) right after this card was introduced from a lesson: introducing a
     * new character drains it to 0 while reviews are due and spends nothing when the queue is
     * empty. Set only on a lesson-enrolled meaning card; null otherwise and on older rows.
     */
    reviewRechargeAfter: integer("review_recharge_after"),

    startedStudyingAt: date("started_studying_at"),
  },
  // `fsrsCardId` backs the `fsrsCard` relation join and the hot
  // `where: { fsrsCardId: { isNotNull: true } }` study-card filter; the date
  // columns back the deck list `isNotNull` filters.
  (table) => [
    index("user_card_deferred_until_idx").on(table.deferredUntil),
    index("user_card_fsrs_card_id_idx").on(table.fsrsCardId),
    index("user_card_queued_at_idx").on(table.queuedAt),
    index("user_card_skipped_at_idx").on(table.skippedAt),
    index("user_card_started_studying_at_idx").on(table.startedStudyingAt),
    // Partial index for the hot study-card read
    // (`fsrs_card_id IS NOT NULL AND deleted_at IS NULL`), used by the study
    // deck list, learning stats, and the learn/drill review-progress due count.
    // Without it the soft-delete + `isNotNull` predicate forced a full
    // `SCAN user_card` (~90ms); the partial index turns it into an index
    // search even though most cards match.
    index("user_card_study_idx")
      .on(table.fsrsCardId)
      .where(sql`${table.fsrsCardId} is not null and ${table.deletedAt} is null`),
  ],
);

export type UserCard = typeof userCardTable.$inferSelect;
